import { Box, Stack } from "@mui/system";
import {
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { validatePlaceholders } from "../../DataStoreHelpers/dataStoreHelpers";
import { useSelector } from "react-redux";
import { setCreateQueryFormDataInput } from "../../../../../features/dataStoreReqSlice";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";

const CreateInput = () => {
  const { createQueryFormData, createQueryFormDataInput } = useSelector(
    (state: any) => state.dataStoreReq
  );
  const dispatch = useDispatch();
  const placeholders = validatePlaceholders(createQueryFormData.requestSql);
  const placeholderCount = placeholders?.placeholders?.length || 0;

  const handleInputChange = (id: string, value: any) => {
    dispatch(
      setCreateQueryFormDataInput({
        [id]: value,
      })
    );
  };

  // Mount/update logic (depends on placeholderCount)
  useEffect(() => {
    const cleanedInputs = Object.entries(createQueryFormDataInput).reduce(
      (acc: any, [key, value]) => {
        const index = parseInt(key.split("-")[0], 10);
        if (!isNaN(index) && index < placeholderCount) {
          const format = createQueryFormDataInput[`${index}-format`];
          if (key.endsWith("value_for_test")) {
            acc[key] =
              format === "array"
                ? Array.isArray(value)
                  ? value
                  : [""]
                : value || "";
          } else {
            acc[key] = value || "";
          }
        }
        return acc;
      },
      {} as Record<string, any>
    );

    dispatch(
      setCreateQueryFormDataInput({
        __replace: true,
        data: cleanedInputs,
      })
    );
    // eslint-disable-next-line
  }, [placeholderCount]);

  const handleAddArrayValue = (index: number) => {
    const key = `${index}-value_for_test`;
    const currentArray = createQueryFormDataInput[key] || [];
    if (currentArray.length === 0) {
      handleInputChange(key, ["", ""]);
    } else {
      handleInputChange(key, [...currentArray, ""]);
    }
  };

  const handleRemoveArrayValue = (index: number, arrayIndex: number) => {
    const newArray = createQueryFormDataInput[`${index}-value_for_test`].filter(
      (_: string, i: number) => i !== arrayIndex
    );
    handleInputChange(`${index}-value_for_test`, newArray);
  };

  return (
    <>
      {placeholderCount > 0 ? (
        <Box>
          {Array.isArray(placeholders?.placeholders) &&
            placeholders.placeholders.map((input, index) => (
              <Box key={index} mb={4} p={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                  mt={2}
                >
                  <Typography fontWeight={600}>{input}</Typography>
                  <FormGroup>
                    <Stack direction="row" spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={
                              createQueryFormDataInput[`${index}-uniq`] || false
                            }
                            onChange={(e) =>
                              handleInputChange(
                                `${index}-uniq`,
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="UNIQ"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={
                              createQueryFormDataInput[`${index}-notMutable`] ||
                              false
                            }
                            onChange={(e) =>
                              handleInputChange(
                                `${index}-notMutable`,
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Not Mutable"
                      />
                    </Stack>
                  </FormGroup>
                </Stack>

                <Grid container spacing={2} mb={2}>
                  <Grid size={6}>
                    <TextField
                      id={`${index}-title`}
                      label="Title"
                      fullWidth
                      size="small"
                      color="secondary"
                      placeholder="Please enter Title"
                      inputProps={{ maxLength: 256 }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) =>
                        handleInputChange(`${index}-title`, e.target.value)
                      }
                      value={createQueryFormDataInput[`${index}-title`] || ""}
                      required
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      label="Description"
                      fullWidth
                      size="small"
                      color="secondary"
                      placeholder="Please enter Description"
                      inputProps={{ maxLength: 256 }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) =>
                        handleInputChange(
                          `${index}-description`,
                          e.target.value
                        )
                      }
                      value={
                        createQueryFormDataInput[`${index}-description`] || ""
                      }
                      required
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} mb={2}>
                  <Grid size={6}>
                    <Autocomplete
                      options={["string", "number", "date", "array"]}
                      value={createQueryFormDataInput[`${index}-format`] || ""}
                      inputValue={
                        createQueryFormDataInput[`${index}-format`] || ""
                      }
                      onInputChange={(_, newValue) => {
                        handleInputChange(`${index}-format`, newValue);
                        handleInputChange(
                          `${index}-value_for_test`,
                          newValue === "array" ? ["", ""] : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Format"
                          placeholder="Please enter Format"
                          fullWidth
                          size="small"
                          color="secondary"
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      id={`${index}-order`}
                      label="Order"
                      type="number"
                      fullWidth
                      size="small"
                      color="secondary"
                      placeholder="Please enter Order"
                      inputProps={{ min: 0 }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) =>
                        handleInputChange(`${index}-order`, e.target.value)
                      }
                      value={createQueryFormDataInput[`${index}-order`] || ""}
                      required
                    />
                  </Grid>

                  <Grid size={12}>
                    {createQueryFormDataInput[`${index}-format`] === "array" ? (
                      <>
                        <Typography fontWeight={500} mb={1}>
                          Value for test
                        </Typography>
                        <Grid container spacing={2}>
                          {createQueryFormDataInput[
                            `${index}-value_for_test`
                          ]?.map((val: string, arrayIndex: number) => (
                            <Grid size={12} key={arrayIndex}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <TextField
                                  label={`Value ${arrayIndex + 1}`}
                                  fullWidth
                                  size="small"
                                  color="secondary"
                                  value={val}
                                  onChange={(e) =>
                                    handleInputChange(
                                      `${index}-value_for_test`,
                                      [
                                        ...createQueryFormDataInput[
                                          `${index}-value_for_test`
                                        ].slice(0, arrayIndex),
                                        e.target.value,
                                        ...createQueryFormDataInput[
                                          `${index}-value_for_test`
                                        ].slice(arrayIndex + 1),
                                      ]
                                    )
                                  }
                                  required
                                />
                                <IconButton
                                  onClick={() =>
                                    handleRemoveArrayValue(index, arrayIndex)
                                  }
                                  color="error"
                                  size="small"
                                  disabled={
                                    createQueryFormDataInput[
                                      `${index}-value_for_test`
                                    ].length <= 2
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                                {arrayIndex ===
                                  createQueryFormDataInput[
                                    `${index}-value_for_test`
                                  ].length -
                                    1 && (
                                  <Button
                                    onClick={() => handleAddArrayValue(index)}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    disabled={!val?.trim()}
                                  >
                                    +
                                  </Button>
                                )}
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    ) : (
                      <TextField
                        id={`${index}-value_for_test`}
                        disabled={!createQueryFormDataInput[`${index}-format`]}
                        label=" Value for test"
                        fullWidth
                        size="small"
                        color="secondary"
                        placeholder="Please enter Default Value"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 256 }}
                        onChange={(e) =>
                          handleInputChange(
                            `${index}-value_for_test`,
                            e.target.value
                          )
                        }
                        value={
                          createQueryFormDataInput[`${index}-value_for_test`] ||
                          ""
                        }
                        required
                      />
                    )}
                  </Grid>
                </Grid>

                <Typography
                  variant="body2"
                  fontSize="12px"
                  color="#00000099"
                  mt={1}
                >
                  All fields are mandatory.
                </Typography>
              </Box>
            ))}
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" fontSize="12px" color="#00000099" m={2}>
            No input fields available.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CreateInput;
